import { IsPackageComponentContext } from 'context/IsPackageComponentContext';
import { useCallback, useContext } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { loadNewPage } from 'store/slices/pageListSlice';
import {
	DEPEND_CONDITION_RULES,
	hasDependencyCondition,
	isFieldHidden,
	isPageHidden,
} from '../services/DependencyService';
import { isPackageStepByPageId } from '../services/PackageService';
import { getActivePage } from '../services/PageService';
import { useFormSubmit } from './useFormSubmit';
import { usePageParam } from './usePageParam';

export const getFormFieldId = (field?: FieldsContent): string | undefined =>
	field?.content?.uniqueIdentifier?.uid || field?.content?.name;

const pageHasVisibleField = (page: StepBlock, fieldId: string): boolean =>
	page.fields?.items?.some((candidate) => {
		if (getFormFieldId(candidate) !== fieldId) return false;
		return !isFieldHidden(candidate);
	}) ?? false;

export const isFieldUsedOnOtherVisiblePage = (
	fieldId: string,
	currentPageId: number,
	orderedPageList: number[]
): boolean => {
	for (const pageId of orderedPageList) {
		if (pageId === currentPageId) continue;

		const page = getActivePage(pageId);
		if (!page || isPageHidden(page)) continue;
		if (pageHasVisibleField(page, fieldId)) return true;
	}

	return false;
};

export const useNavigation = () => {
	const dispatch = useAppDispatch();
	const { orderedPageList } = useAppSelector((state) => state.pages);
	const { setPageParam } = usePageParam();

	const { resetFormField } = useFormSubmit();

	const isStepHidden = (pageId: number, forward: boolean) => {
		const activePage = getActivePage(pageId);
		const isHidden = isPageHidden(activePage);
		if (forward && isHidden) {
			if (hasDependencyCondition(activePage?.contentDependency, DEPEND_CONDITION_RULES.EMPTY)) {
				// Empty rules hide prefilled steps; preserve the answer that made the step unnecessary.
				return true;
			}

			// Opportunity descriptions read Redux, so discard answers from skipped branches there.
			activePage.fields?.items?.forEach((field) => {
				const formFieldId = getFormFieldId(field);
				// This clears Redux only, not the mounted step's React Hook Form state.
				if (hasDependencyCondition(field?.content?.contentDependency, DEPEND_CONDITION_RULES.EMPTY)) {
					return;
				}
				// Mutually exclusive steps can share a UID, such as current/past insurer selection.
				const usedOnVisiblePage = formFieldId
					? isFieldUsedOnOtherVisiblePage(formFieldId, pageId, orderedPageList)
					: false;
				if (formFieldId && !usedOnVisiblePage) {
					// TODO(CD-07): Also invalidate the owning mounted form so resubmission cannot restore this answer.
					resetFormField(formFieldId);
				}
			});
		}
		return isHidden;
	};

	const { setIsVisible } = useContext(IsPackageComponentContext);
	const navigate = useCallback(
		(pageId: number, forward: boolean) => {
			const currentInx = orderedPageList.indexOf(pageId);
			let pageList = [...orderedPageList];

			if (forward) {
				pageList = pageList.splice(currentInx + 1);
			} else {
				pageList = pageList.splice(0, currentInx);
				pageList.reverse();
			}

			const navigateTo = pageList.find((id) => !isStepHidden(id, forward));

			if (navigateTo) {
				setTimeout(() => {
					setIsVisible(isPackageStepByPageId(navigateTo));
				}, 100);
			}

			navigateTo ? setPageParam(navigateTo.toString()) : dispatch(loadNewPage());
		},
		[dispatch, isStepHidden, orderedPageList, setIsVisible, setPageParam]
	);

	return { navigate };
};
